import { useEffect, useState } from "react";
import { X, ArrowRight, Sparkles, Phone } from "lucide-react";
import { CONTACT } from "@/lib/assets";

const STORAGE_KEY = "hs-popup-dismissed-v1";
const TIME_TRIGGER_MS = 15000;
const SCROLL_TRIGGER_PCT = 0.6;

const logStorageError = (err) => {
  if (process.env.NODE_ENV === "development") {
    console.warn("sessionStorage unavailable:", err);
  }
};

const isDismissed = () => {
  try {
    return !!sessionStorage.getItem(STORAGE_KEY);
  } catch (err) {
    logStorageError(err);
    return false;
  }
};

const markDismissed = () => {
  try {
    sessionStorage.setItem(STORAGE_KEY, "1");
  } catch (err) {
    logStorageError(err);
  }
};

// Opens the popup after 15s or at 60% scroll, whichever comes first.
function usePopupTrigger(setOpen) {
  useEffect(() => {
    if (typeof window === "undefined" || isDismissed()) return;

    let triggered = false;
    const trigger = () => {
      if (triggered) return;
      triggered = true;
      setOpen(true);
    };

    const timer = setTimeout(trigger, TIME_TRIGGER_MS);

    const onScroll = () => {
      const h = document.documentElement;
      const max = h.scrollHeight - window.innerHeight;
      if (max <= 0) return;
      if (window.scrollY / max >= SCROLL_TRIGGER_PCT) trigger();
    };
    window.addEventListener("scroll", onScroll, { passive: true });

    return () => {
      clearTimeout(timer);
      window.removeEventListener("scroll", onScroll);
    };
  }, [setOpen]);
}

export default function LeadPopup() {
  const [open, setOpen] = useState(false);
  usePopupTrigger(setOpen);

  const close = () => {
    setOpen(false);
    markDismissed();
  };

  const goContact = () => {
    close();
    setTimeout(() => {
      document.getElementById("kontakt")?.scrollIntoView({ behavior: "smooth" });
    }, 80);
  };

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") {
        setOpen(false);
        markDismissed();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center p-4 sm:p-6"
      data-testid="lead-popup"
      role="dialog"
      aria-modal="true"
      aria-labelledby="lead-popup-title"
    >
      <button
        aria-label="Lukk"
        onClick={close}
        className="absolute inset-0 bg-black/70 backdrop-blur-md animate-in fade-in"
        data-testid="lead-popup-backdrop"
      />
      <PopupCard onClose={close} onCta={goContact} />
    </div>
  );
}

const POINTS = [
  "Lokal håndverker i Sveio",
  "Konkret tilbud innen få dager",
  "Vi tar hele jobben – fra A til Å",
];

function PopupCard({ onClose, onCta }) {
  return (
    <div
      className="relative w-full max-w-xl rounded-3xl border hairline overflow-hidden grain glow-cyan animate-in zoom-in-95 fade-in duration-300"
      style={{ background: "linear-gradient(140deg, hsl(var(--hs-bg-3)) 0%, hsl(var(--hs-bg-2)) 100%)" }}
    >
      <div className="pointer-events-none absolute -top-24 -right-24 w-72 h-72 rounded-full bg-cyan/30 blur-[100px]" />
      <div className="pointer-events-none absolute -bottom-28 -left-24 w-72 h-72 rounded-full bg-coral/25 blur-[110px]" />

      <button
        onClick={onClose}
        aria-label="Lukk"
        className="absolute top-4 right-4 z-10 w-9 h-9 rounded-full grid place-items-center text-white/70 hover:text-white bg-white/5 hover:bg-white/10 transition"
        data-testid="lead-popup-close"
      >
        <X className="w-4 h-4" />
      </button>

      <div className="relative p-7 sm:p-10">
        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full glass mono text-[11px] uppercase tracking-[0.22em] text-cyan mb-6">
          <Sparkles className="w-3.5 h-3.5" />
          Begrenset tilbud
        </div>

        <h3
          id="lead-popup-title"
          className="text-3xl sm:text-[40px] leading-[1.02] font-bold text-white mb-4"
          data-testid="lead-popup-title"
        >
          Gratis befaring –<br />
          <span className="text-gradient">helt uforpliktende.</span>
        </h3>

        <p className="text-white/70 leading-relaxed mb-7 max-w-md">
          Vi kommer hjem til deg, kartlegger boligen og foreslår løsninger
          som faktisk lønner seg. Ingen skjulte kostnader, ingen press.
        </p>

        <ul className="space-y-2 mb-8">
          {POINTS.map((p) => (
            <li key={p} className="flex items-center gap-3 text-white/85 text-[15px]">
              <span className="w-1.5 h-1.5 rounded-full bg-cyan shrink-0" />
              {p}
            </li>
          ))}
        </ul>

        <div className="flex flex-col sm:flex-row gap-3">
          <button
            onClick={onCta}
            className="btn btn-primary justify-center"
            data-testid="lead-popup-cta"
          >
            Be om befaring nå
            <ArrowRight className="w-4 h-4" />
          </button>
          <a
            href={`tel:${CONTACT.phoneRaw}`}
            onClick={onClose}
            className="btn btn-ghost justify-center"
            data-testid="lead-popup-call"
          >
            <Phone className="w-4 h-4" />
            Ring {CONTACT.phone}
          </a>
        </div>

        <button
          onClick={onClose}
          className="mt-5 mono text-[11px] uppercase tracking-[0.22em] text-white/40 hover:text-white/70 transition"
          data-testid="lead-popup-dismiss"
        >
          Nei takk, jeg ser meg bare rundt
        </button>
      </div>
    </div>
  );
}
